import React from 'react';
import { PortableText } from '@portabletext/react';
import { getIslandDividerLogoSrc } from 'alakai-aloha-shared';
import {
  ItineraryAnatomySection,
  ItineraryPlaybookPageView,
  ItineraryWorkflowSection,
  WhyAlakaiSection,
} from 'alakai-aloha-shared/public-ui';
import { urlFor } from '../../lib/sanityImage';
import { getEntityPreview } from '../../services/cms/getEntityPreview';
import { getRuntimeSiteContext } from '../../lib/siteContext';

const INTERNAL_DOC_PATHS = {
  activity: '/activities',
  restaurant: '/restaurants',
  lodging: '/lodging',
  blogPost: '/blog',
  area: '/areas',
  region: '/regions',
};

const portableTextComponents = {
  marks: {
    link: ({ value, children }) => (
      <a
        href={value?.href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-sky-300 underline hover:text-sky-200"
      >
        {children}
      </a>
    ),
  },
  block: {
    h2: ({ children }) => <h2 className="text-lg font-semibold mt-5 mb-2 text-white">{children}</h2>,
    h3: ({ children }) => <h3 className="text-sm font-semibold mt-4 mb-1.5 text-white/90">{children}</h3>,
    normal: ({ children }) => <p className="mb-1.5 text-xs md:text-sm leading-relaxed text-white/80">{children}</p>,
  },
  list: {
    bullet: ({ children }) => <ul className="list-disc ml-5 space-y-1 text-xs md:text-sm leading-relaxed text-white/80">{children}</ul>,
    number: ({ children }) => <ol className="list-decimal ml-5 space-y-1 text-xs md:text-sm leading-relaxed text-white/80">{children}</ol>,
  },
};

function resolveInternalLink(link) {
  if (!link || link.kind !== 'internal') {
    return null;
  }

  const basePath = INTERNAL_DOC_PATHS[link.docType];
  if (!basePath || !link.slug) {
    return null;
  }

  return `${basePath}/${link.slug}`;
}

function badgeFor(docType, kind) {
  if (kind === 'external') {
    return 'External';
  }

  switch (docType) {
    case 'activity':
      return 'Activity';
    case 'restaurant':
      return 'Dining';
    case 'lodging':
      return 'Stay';
    case 'area':
      return 'Area';
    case 'region':
      return 'Region';
    case 'blogPost':
      return 'Guide';
    default:
      return null;
  }
}

function mapSupportingLink(link) {
  if (!link) return null;

  const href = link.kind === 'external' ? link.url : resolveInternalLink(link);
  const label = link.label || link.title;
  if (!href || !label) return null;

  return {
    key: link._key,
    href,
    label,
    badge: badgeFor(link.docType, link.kind),
  };
}

export default function ItineraryPlaybookPageAdapter({
  playbook,
  primaryCtaLabel,
  finalCtaLabel,
  finalCtaHeading,
  onCtaClick,
}) {
  const { islandConfig } = React.useMemo(() => getRuntimeSiteContext(), []);
  const sharedPlaybook = {
    title: playbook.title,
    heroSubhead: playbook.hero?.subhead,
    heroImageUrl: playbook.hero?.image?.asset
      ? urlFor(playbook.hero.image).width(2400).height(1350).fit('crop').auto('format').url()
      : undefined,
    heroImageAlt: playbook.hero?.image?.alt || playbook.hero?.imageAlt || playbook.title,
    who: Array.isArray(playbook.who) ? playbook.who : [],
    timingTips: playbook.pacingSeason ? (
      <PortableText value={playbook.pacingSeason} components={portableTextComponents} />
    ) : null,
    anchorDays: Array.isArray(playbook.anchorDays)
      ? playbook.anchorDays.map((day) => ({
          key: day._key,
          title: day.title,
          body: <PortableText value={day.body} components={portableTextComponents} />,
          links: Array.isArray(day.links) ? day.links.map(mapSupportingLink).filter(Boolean) : [],
          imageUrl: day.image?.asset
            ? urlFor(day.image).width(1400).height(900).fit('crop').auto('format').url()
            : undefined,
          imageAlt: day.image?.alt || day.title,
        }))
      : [],
    modules: Array.isArray(playbook.modules)
      ? playbook.modules.map((module) => {
          const primaryLink = Array.isArray(module.links) && module.links.length > 0 ? module.links[0] : null;
          const mappedPrimaryLink = mapSupportingLink(primaryLink);
          return {
            key: module._key,
            title: module.title,
            when: module.when,
            body: <PortableText value={module.body} components={portableTextComponents} />,
            imageUrl: primaryLink?.image?.asset
              ? urlFor(primaryLink.image).width(1200).height(800).fit('crop').auto('format').url()
              : undefined,
            imageAlt: primaryLink?.image?.alt || primaryLink?.label || module.title,
            primaryLink: mappedPrimaryLink ? { ...mappedPrimaryLink, label: mappedPrimaryLink.label || 'Explore' } : null,
          };
        })
      : [],
    faqs: Array.isArray(playbook.faq)
      ? playbook.faq.map((faqItem) => ({
          key: faqItem._key,
          question: faqItem.question,
          answer: <PortableText value={faqItem.answer} components={portableTextComponents} />,
        }))
      : [],
  };

  return (
    <ItineraryPlaybookPageView
      playbook={sharedPlaybook}
      primaryCtaLabel={primaryCtaLabel}
      finalCtaLabel={finalCtaLabel}
      finalCtaHeading={finalCtaHeading}
      onCtaClick={onCtaClick}
      sectionDividerLogoSrc={getIslandDividerLogoSrc(islandConfig.key)}
      workflowSlot={
        <ItineraryWorkflowSection
          primaryCtaLabel={finalCtaLabel}
          onPrimaryCtaClick={onCtaClick}
          ctaLocation="workflow_primary"
        />
      }
      anatomySlot={
        <ItineraryAnatomySection
          primaryCtaLabel={finalCtaLabel}
          onPrimaryCtaClick={onCtaClick}
          ctaLocation="anatomy_primary"
          loadEntityPreview={getEntityPreview}
        />
      }
      whyAlakaiSlot={
        <WhyAlakaiSection
          items={[
            {
              icon: 'listen',
              title: 'We listen',
              body: 'Every itinerary starts with a thorough interview designed to truly understand you and your traveling group.',
              imageSrc: '/images/why-alakai/Interview.jpg',
            },
            {
              icon: 'bespoke',
              title: 'Bespoke itineraries',
              body: "Don't settle for off-the-rack templates built for the average tourist.",
              imageSrc: '/images/why-alakai/Itinerary.jpg',
            },
            {
              icon: 'library',
              title: 'In-depth profiles',
              body: `Every itinerary deep-links to the web's most comprehensive library of ${islandConfig.title} content.`,
              imageSrc: '/images/why-alakai/Profiles.jpg',
            },
          ]}
          proof={[
            { label: 'Restaurant profiles', to: '/restaurants', imageSrc: '/images/why-alakai/restaurant-profiles.jpg' },
            { label: 'Activity profiles', to: '/activities', imageSrc: '/images/why-alakai/Activity-profiles.jpg' },
            { label: 'Lodging profiles', to: '/lodging', imageSrc: '/images/why-alakai/Lodging-profiles.jpg' },
            { label: 'Region guide', to: '/regions', imageSrc: '/images/why-alakai/Region-guide.jpg' },
            { label: 'Area profiles', to: '/regions', imageSrc: '/images/why-alakai/Area-profiles.jpg' },
            { label: 'Blog', to: '/blog', imageSrc: '/images/why-alakai/Blog.jpg' },
          ]}
          cta={false}
        />
      }
    />
  );
}
